import type { EnrichmentStageReport } from '../../hooks/useEnrichmentStream';
import { colors } from '../../theme/colors';
import { Box } from '../primitives/Box';
import { VStack } from '../primitives/Stack';
import { Text } from '../primitives/Text';

interface EnrichmentStageBreakdownProps {
  stages?: EnrichmentStageReport[];
  t: (key: string) => string;
}

const STAGE_MARKS: Record<EnrichmentStageReport['status'], string> = {
  success: '✓',
  failed: '✕',
  skipped: '—',
  not_reached: '—',
};

const STAGE_MARK_COLORS: Record<EnrichmentStageReport['status'], string> = {
  success: colors.status.success.main,
  failed: colors.status.error.main,
  skipped: colors.text.tertiary,
  not_reached: colors.text.tertiary,
};

export function EnrichmentStageBreakdown({ stages, t }: Readonly<EnrichmentStageBreakdownProps>) {
  if (!stages || stages.length === 0) return null;

  return (
    <VStack spacing="sm">
      <Text variant="bodySmall" color={colors.text.secondary} weight="semibold">
        {t('workflow.stages.title')}
      </Text>
      {stages.map((report) => {
        const label = t(`workflow.stages.${report.stage}`);
        const statusLabel = t(`workflow.stageStatus.${report.status}`);

        return (
          <Box
            key={report.stage}
            flexDirection="row"
            alignItems="flex-start"
            gap="sm"
            padding="sm"
            borderRadius="md"
            borderWidth={1}
            borderColor={
              report.status === 'failed' ? colors.status.error.main : colors.border.subtle
            }
            accessibilityLabel={`${label}: ${statusLabel}`}
          >
            <Text
              variant="bodySmall"
              color={STAGE_MARK_COLORS[report.status]}
              weight="bold"
              accessibilityElementsHidden
            >
              {STAGE_MARKS[report.status]}
            </Text>
            <VStack spacing="xs" style={{ flex: 1 }}>
              <Box flexDirection="row" justifyContent="space-between" gap="sm">
                <Text variant="bodySmall" color={colors.text.primary}>
                  {label}
                </Text>
                <Text variant="caption" color={STAGE_MARK_COLORS[report.status]}>
                  {statusLabel}
                </Text>
              </Box>
              {report.status === 'failed' && (
                <Text variant="caption" color={colors.text.secondary}>
                  {t(`workflow.stageReasons.${report.reasonCode ?? 'unknown'}`)}
                </Text>
              )}
            </VStack>
          </Box>
        );
      })}
    </VStack>
  );
}
